// src/features/projects/hub/ProjectsHubBar.jsx
// Under-title lens tabs and normal-flow current-week strip. Counts come from the page
// (useLandingWeekly) so the strip and alert chip share one current-week read.
import React from "react";
import { T } from "../../../app/tokens.js";
import { HUB_LENS } from "./hubAccess.js";
import { useFeatureFlagContext } from "../../../contexts/FeatureFlagContext.jsx";
import { projectsPresentationTabs } from "../../../app/projectsTabs.js";
import "./ProjectsHubBar.css";
import { ChromeTab } from "../../../components/ObjectChrome.jsx";

export const THIS_WEEK_COUNTS_HELP = "Current-assignment semantics, read-only. Of: distinct people actively assigned to the active projects in view (including owner-only assignments). Posted: distinct people with an attributed track line this week on a project and track they are currently assigned to; owner-only assignments without a track cannot post.";
export const THIS_WEEK_DECISIONS_HELP = "Open decisions on the same active projects with a needed-by date in this week (Monday through Sunday).";

function LensTab({ id, label, active, onSelect, disabled = false }) {
  return (
    <ChromeTab active={active}
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      className="projects-hub-lens"
      data-testid={`projects-lens-${id}`}
      onClick={() => onSelect(id)}
      onKeyDown={(e) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
        e.preventDefault();
        const tabs = [...e.currentTarget.parentElement.querySelectorAll('[role="tab"]:not(:disabled)')];
        const index = tabs.indexOf(e.currentTarget);
        const next = e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1
          : (index + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].focus();
        tabs[next].click();
      }}
      style={{
        "--hub-focus": T.accent,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {label}
    </ChromeTab>
  );
}

export function ThisWeekStrip({ weekly, onOpenWeekly }) {
  const w = weekly || { loading: true };
  const d = w.data;
  const fig = { fontWeight: T.fwBold };
  let body;
  if (w.empty) body = <span data-testid="projects-this-week-empty">This week · no active projects in view</span>;
  else if (w.error) body = (
    <span role="alert" data-testid="projects-this-week-error">
      This week · {typeof w.error === "string" ? w.error : "Counts unavailable."}
      {w.retry && <button type="button" className="projects-strip-link" data-testid="projects-this-week-retry" onClick={w.retry} style={{ marginLeft: 6 }}>Retry</button>}
    </span>
  );
  else if (w.loading || !d) body = <span data-testid="projects-this-week-loading">This week · loading counts</span>;
  else body = (
    <>
      This week ·{" "}
      <span data-testid="projects-this-week-counts" title={THIS_WEEK_COUNTS_HELP}>
        <span style={fig}>{d.posted}</span> of <span style={fig}>{d.eligible}</span> posted
      </span>
      {d.eligible === 0 && <span data-testid="projects-this-week-no-contributors">{" (no eligible contributors)"}</span>}
      {" · "}
      <span data-testid="projects-this-week-decisions" title={THIS_WEEK_DECISIONS_HELP}>
        <span style={fig}>{d.decisionsDue}</span> decision{d.decisionsDue === 1 ? "" : "s"}
      </span>
    </>
  );
  return (
    <div
      data-testid="projects-this-week"
      style={{
        display: "inline-flex", alignItems: "center", gap: 0, flexWrap: "wrap",
        padding: "5px 11px", borderRadius: 8, minHeight: 32,
        background: T.projectsStripBg, border: `1px solid ${T.projectsStripBorder}`,
        color: T.projectsStripText, fontSize: T.fsSm,
      }}
    >
      <span data-testid="projects-this-week-title">{body}</span>
      {onOpenWeekly && <><span aria-hidden="true" style={{ whiteSpace: "pre" }}>{" · "}</span>
      <button type="button" className="projects-strip-link" data-testid="projects-open-weekly" onClick={onOpenWeekly}>
        Open Weekly Review →
      </button></>}
    </div>
  );
}

export function ProjectsHubBar({ lens, onLensChange, showFinancials, weekly, navigationOnly = false }) {
  const { projectsTabs } = useFeatureFlagContext();
  const tabs = projectsPresentationTabs(projectsTabs).filter((row) => row.enabled
    && (row.lens !== HUB_LENS.FINANCIALS || showFinancials));
  return (
    <div data-testid="projects-hub" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", minWidth: 0 }}>
      <div role="tablist" aria-label="Projects lenses" className="projects-hub-tablist object-subnav-tabs">
        {tabs.map(({ lens: id, label }) => (
          <LensTab key={id} id={id} label={label} active={lens === id} onSelect={onLensChange} />
        ))}
      </div>
      {!navigationOnly && <><div style={{ flex: 1 }} />
      <ThisWeekStrip weekly={weekly} onOpenWeekly={tabs.some((row) => row.lens === HUB_LENS.WEEKLY)
        ? () => onLensChange(HUB_LENS.WEEKLY) : undefined} />
      </>}
    </div>
  );
}
